// import node module libraries
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Col, Form, Row, Table} from 'react-bootstrap';
import {useFilters, useGlobalFilter, usePagination, useTable} from 'react-table';
import {FormSelect} from 'components/elements/form-select/FormSelect';
import {format, parse} from 'date-fns';
import frLocale from 'date-fns/locale/fr';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import data files
import BulletinpaieService from '../../services/BulletinpaieService';
import PersonnelService from '../../services/PersonnelService';
import {Link} from 'react-router-dom';

const BulletinpaieListItems = () => {
    const [originalBulletinpaies, setOriginalBulletinpaies] = useState([]);
    const [bulletinpaies, setBulletinpaies] = useState([]);
    const [personnel, setPersonnel] = useState([]);
    const [periodeOptions, setPeriodeOptions] = useState([]);

    useEffect(() => {
        Promise.all([
            BulletinpaieService.getAllBulletinpaies(),
            PersonnelService.getAllPersonnels()
        ]).then(([bulletinpaiesData, personnelData]) => {
            setOriginalBulletinpaies(bulletinpaiesData);
            setBulletinpaies(bulletinpaiesData);
            setPersonnel(personnelData);

            // Extract unique formatted periode_paie from bulletinpaiesData
            const periodeChoices = [
                ...new Set(
                    bulletinpaiesData.map((bulletinpaie) =>
                        formatPeriodePaieBulletinpaie(bulletinpaie.periode_paie)
                    )
                )
            ];
            setPeriodeOptions(
                periodeChoices.map((value) => ({value, label: value}))
            );
        });
    }, []);

    const PERIODE_CHOICES = periodeOptions;

    const formatValue = (value, columnId) => {
        if (columnId === 'salaire_net') {
            return new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'XAF'}).format(value);
        }
        return value;
    };

    const getFilterTerm = (event) => {
        let filterTerm = event.target.value;
        if (filterTerm !== '') {
            const newBulletinpaiesList = originalBulletinpaies.filter(
                (bulletinpaie) => {
                    return (
                        formatPeriodePaieBulletinpaie(bulletinpaie.periode_paie) ===
                        filterTerm
                    );
                }
            );
            setBulletinpaies(newBulletinpaiesList);
        } else {
            setBulletinpaies(originalBulletinpaies);
        }
    };

    const formatPeriodePaieBulletinpaie = (value) => {
        const date = parse(value, 'yyyy-MM', new Date());
        const formattedDate = format(date, 'MMMM yyyy', {locale: frLocale});
        return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
    };

    const getPersonnelById = (personnelId) => {
        const personnelObj =
            personnel.find((item) => item.id === personnelId) ?? {};

        return personnelObj ? personnelObj.nom + ' ' + personnelObj.prenom : '';
    };

    const columns = useMemo(
        () => [
            {accessor: 'id', Header: 'ID', show: false},
            {
                accessor: 'personnel',
                Header: 'Personnel',
                Cell: ({value}) => <h5 className="text-uppercase">{value}</h5>
            },

            {
                accessor: 'periode_paie',
                Header: 'Période paiement',
                Cell: ({value}) => <div>{formatPeriodePaieBulletinpaie(value)}</div>
            },
            {
                accessor: 'date_paiement',
                Header: 'Date paiement',
                Cell: ({value}) => <div>{value}</div>
            },
            {
                accessor: 'salaire_net',
                Header: 'Montant',
                Cell: ({value}) => <div>{formatValue(value, 'salaire_net')}</div>
            },
            {
                accessor: 'action',
                Header: '',
                Cell: ({row}) => {
                    return (
                        <div className="d-flex align-items-center">
                            <div className="ms-3">
                                <h4 className="mb-0">
                                    <Link
                                        to={`/administration/personnel/salaires/${row.original.id}/print`}
                                        className="text-inherit"
                                    >
                                        <div className="icon-shape icon-sm rounded-3 border p-4">
                                            <i className="fa-solid fa-eye fa-sm text-muted"></i>
                                        </div>
                                    </Link>
                                </h4>
                            </div>
                        </div>
                    );
                }
            }
        ],
        []
    );

    const data = useMemo(() => {
        return bulletinpaies.map((bulletinpaie) => ({
            ...bulletinpaie,
            personnel: getPersonnelById(bulletinpaie.personnel)
        }));
    }, [bulletinpaies]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        nextPage,
        previousPage,
        state,
        gotoPage,
        pageCount,
        prepareRow,
        setGlobalFilter
    } = useTable(
        {
            columns,
            data,
            initialState: {
                pageSize: 10,
                hiddenColumns: columns.map((column) => {
                    if (column.show === false) return column.accessor || column.id;
                    else return false;
                })
            }
        },
        useFilters,
        useGlobalFilter,
        usePagination
    );

    const {pageIndex, globalFilter} = state;

    return (
        <Fragment>
            <div className="overflow-hidden p-3">
                <Row className="justify-content-md-between mb-4 mb-xl-0 ">
                    <Col xxl={3} lg={4} md={6} xs={12}>
                        <GlobalFilter
                            filter={globalFilter}
                            setFilter={setGlobalFilter}
                            placeholder="Rechercher bulletinpaie"
                        />
                    </Col>
                    <Col xxl={3} lg={4} md={6} xs={12}>
                        {/* records filtering options */}
                        <Form.Control
                            as={FormSelect}
                            placeholder="Filtrer par période"
                            options={PERIODE_CHOICES}
                            onChange={getFilterTerm}
                        />
                    </Col>
                </Row>
            </div>

            <Table
                hover
                responsive
                {...getTableProps()}
                className="table text-nowrap table-centered"
            >
                <thead className="table-light">
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => {
                                return (
                                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                                );
                            })}
                        </tr>
                    );
                })}
                </tbody>
            </Table>

            <Pagination
                previousPage={previousPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                gotoPage={gotoPage}
                nextPage={nextPage}
            />
        </Fragment>
    );
};

export default BulletinpaieListItems;
